import { VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline, TextLabel2 } from '@cbhq/cds-web/typography';
import { RemoteImage } from '@cbhq/cds-web/media';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { memo, useCallback } from 'react';
import { DisplayType, format } from '@/utils/numberFormatter';
import type { TokenizedEquityData } from './hooks/useTokenizedEquitiesData';

type Props = {
  equity: TokenizedEquityData;
  onSelect?: (tokenAddress: string) => void;
};

/**
 * TokenizedEquitiesMobileRow - Mobile view for a single tokenized equity
 *
 * Displays equity information in a compact ListCell format optimized for mobile screens.
 * Shows token name/symbol, stock price with change indicator, and token price.
 */
export const TokenizedEquitiesMobileRow = memo(function TokenizedEquitiesMobileRow({
  equity,
  onSelect,
}: Props) {
  const imageUrl = equity.imageUrl;

  const isPositive = equity.stockPriceChange > 0;
  const changeColor = isPositive ? 'fgPositive' : 'fgNegative';
  const changeIcon = isPositive ? '↗' : '↘';

  const handlePress = useCallback(() => {
    onSelect?.(equity.tokenAddress);
  }, [onSelect, equity.tokenAddress]);

  return (
    <ListCell
      testID={`equity-row-${equity.symbol}`}
      justifyContent="space-between"
      onClick={handlePress}
      media={<RemoteImage source={imageUrl} size="l" shape="circle" alt={equity.name} />}
      title={<TextHeadline as="span">{equity.name}</TextHeadline>}
      description={
        <TextBody as="span" color="fgMuted">
          {equity.symbol}
        </TextBody>
      }
      detail={
        <VStack alignItems="flex-end" gap={0}>
          <TextBody as="span">
            {format({ value: equity.stockPriceUsd, type: DisplayType.Fiat })}
          </TextBody>
          <TextLabel2 as="span" color={changeColor}>
            {changeIcon} {Math.abs(equity.stockPriceChange)}%
          </TextLabel2>
        </VStack>
      }
    />
  );
});
